<script setup>
import { h, computed } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import TableFooterPagination from 'dashboard/components/widgets/TableFooterPagination.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalPages: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['pageChange']);

const { t } = useI18n();

const defaultSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? 'text-n-slate-12' : 'text-n-slate-11',
    },
    cellProps.getValue() ?? '---'
  );

const percentageRender = cellProps => {
  const value = cellProps.getValue();
  return h(
    'span',
    {
      class: 'text-n-slate-12',
    },
    value != null ? `${value}%` : '---'
  );
};

const columnHelper = createColumnHelper();

const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t('CLASSIFICATION_REPORTS.TABLE.HEADER.CLASSIFICATION'),
    cell: defaultSpanRender,
    size: 300,
  }),
  columnHelper.accessor('count', {
    header: t('CLASSIFICATION_REPORTS.TABLE.HEADER.COUNT'),
    cell: defaultSpanRender,
    size: 100,
  }),
  columnHelper.accessor('percentage', {
    header: t('CLASSIFICATION_REPORTS.TABLE.HEADER.PERCENTAGE'),
    cell: percentageRender,
    size: 80,
  }),
]);

const table = useVueTable({
  get data() {
    return props.data;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

const onPageChange = page => {
  emit('pageChange', page);
};
</script>

<template>
  <div class="flex flex-col flex-1">
    <div
      v-if="isLoading"
      class="flex items-center justify-center p-8 text-base"
    >
      <Spinner />
      <span class="ml-2">
        {{ $t('CLASSIFICATION_REPORTS.LOADING') }}
      </span>
    </div>
    <template v-else-if="data.length">
      <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />
      <div v-if="totalPages > 1" class="flex justify-end py-3">
        <TableFooterPagination
          :current-page="currentPage"
          :total-pages="totalPages"
          @page-change="onPageChange"
        />
      </div>
    </template>
    <EmptyState v-else :title="$t('CLASSIFICATION_REPORTS.NO_RECORDS')" />
  </div>
</template>
